import { useEffect, useState } from 'react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { supabase } from '@/lib/supabase';
import { DocumentTagsEditor } from '@/components/connections/DocumentTagsEditor';

/** D1 — a document cited by the chat, opened over Profil › Documents (`?doc=`). */
export const KnowledgeDocumentDrawer = ({ documentId, userId, isManager, onClose }: {
  documentId: string;
  userId: string | null;
  isManager: boolean;
  onClose: () => void;
}) => {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'missing' }
    | { status: 'ready'; title: string; folder: string; content: string; canEdit: boolean }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void supabase
      .from('knowledge_documents')
      .select('title, content, knowledge_folders!inner(name, kind, owner_user_id)')
      .eq('id', documentId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setState({ status: 'missing' });
          return;
        }
        const raw = Array.isArray(data.knowledge_folders) ? data.knowledge_folders[0] : data.knowledge_folders;
        const folder = (raw ?? {}) as { name?: string; kind?: string; owner_user_id?: string };
        setState({
          status: 'ready',
          title: String(data.title ?? ''),
          folder: folder.name ?? '',
          content: String(data.content ?? ''),
          // Same rule as the RLS: a personal folder's owner, a manager for a shared one.
          canEdit: folder.kind === 'personal' ? folder.owner_user_id === userId : isManager,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, isManager, userId]);

  return (
    <SideDrawer label="Document" title={state.status === 'ready' ? state.title : undefined} onClose={onClose}>
      <div className="px-4 py-5 sm:px-6">
        {state.status === 'loading' && (
          <SkeletonGroup label="Chargement du document">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="mt-4 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-11/12" />
            <Skeleton className="mt-2 h-4 w-4/5" />
          </SkeletonGroup>
        )}
        {state.status === 'missing' && <p className="text-sm text-domino-inkSoft">Ce document n’existe plus ou ne vous est pas accessible.</p>}
        {state.status === 'ready' && (
          <>
            <p className="text-sm text-domino-inkSoft">{state.folder}</p>
            <div className="mt-3"><DocumentTagsEditor documentId={documentId} canEdit={state.canEdit} isManager={isManager} /></div>
            <div className="mt-4 whitespace-pre-wrap text-sm leading-6 text-domino-ink">{state.content}</div>
          </>
        )}
      </div>
    </SideDrawer>
  );
};
